import { useState, useEffect } from "react";
import { Search, Filter, Calendar, FileText, Receipt, Package, ClipboardCheck, X, Download, SlidersHorizontal, Euro } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { authFetch } from "../services/authService";
import { PdfArchiveSection } from "../components/PdfArchiveSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Archive({ user }) {
  const [results, setResults] = useState({
    appointments: [],
    acceptances: [],
    quotes: [],
    invoices: [],
    supplier_orders: []
  });
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({
    doc_type: "",
    search: "",
    status: "",
    date_from: "",
    date_to: "",
    customer_name: "",
    customer_id: "",
    vehicle_plate: "",
    min_amount: "",
    max_amount: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await authFetch(`${API}/customers`, { });
      if (response.ok) {
        setCustomers(await response.json());
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder);

      const response = await authFetch(`${API}/archive/search?${params}`, {
      });

      if (response.ok) {
        const data = await response.json();
        // Apply client-side amount filtering if needed
        if (filters.min_amount || filters.max_amount) {
          const min = parseFloat(filters.min_amount) || 0;
          const max = parseFloat(filters.max_amount) || Infinity;
          
          if (data.quotes) {
            data.quotes = data.quotes.filter(q => {
              const total = q.total || 0;
              return total >= min && total <= max;
            });
          }
          if (data.invoices) {
            data.invoices = data.invoices.filter(i => {
              const total = i.total || 0;
              return total >= min && total <= max;
            });
          }
        }
        setResults(data);
      }
    } catch (error) {
      toast.error("Errore nella ricerca");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      doc_type: "",
      search: "",
      status: "",
      date_from: "",
      date_to: "",
      customer_name: "",
      customer_id: "",
      vehicle_plate: "",
      min_amount: "",
      max_amount: ""
    });
  };

  const exportResults = () => {
    const allResults = [];
    
    Object.entries(results).forEach(([type, docs]) => {
      docs.forEach(doc => {
        allResults.push({
          tipo: type,
          numero: doc.quote_number || doc.invoice_number || doc.order_number || doc.vehicle_plate || doc.appointment_date || '-',
          cliente: doc.customer_name || doc.supplier_name || '-',
          stato: doc.status || '-',
          totale: doc.total || '-',
          data: doc.created_at?.split('T')[0] || '-'
        });
      });
    });

    if (allResults.length === 0) {
      toast.error("Nessun risultato da esportare");
      return;
    }

    // Create CSV
    const headers = ['Tipo', 'Numero', 'Cliente', 'Stato', 'Totale', 'Data'];
    const csvContent = [
      headers.join(','),
      ...allResults.map(r => Object.values(r).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `archivio_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success("Export completato");
  };

  const docTypes = [
    { value: "", label: "Tutti i Documenti" },
    { value: "appointments", label: "Appuntamenti", icon: Calendar },
    { value: "acceptances", label: "Accettazioni", icon: ClipboardCheck },
    { value: "quotes", label: "Preventivi", icon: FileText },
    { value: "invoices", label: "Fatture", icon: Receipt },
    { value: "supplier_orders", label: "Ordini Fornitore", icon: Package }
  ];

  const statusOptions = {
    appointments: [
      { value: "", label: "Tutti" },
      { value: "scheduled", label: "Programmato" },
      { value: "in_progress", label: "In Corso" },
      { value: "completed", label: "Completato" },
      { value: "cancelled", label: "Annullato" }
    ],
    acceptances: [
      { value: "", label: "Tutti" },
      { value: "received", label: "Ricevuto" },
      { value: "in_diagnosis", label: "In Diagnosi" },
      { value: "quote_sent", label: "Preventivo Inviato" },
      { value: "in_repair", label: "In Riparazione" },
      { value: "completed", label: "Completato" },
      { value: "delivered", label: "Consegnato" }
    ],
    quotes: [
      { value: "", label: "Tutti" },
      { value: "draft", label: "Bozza" },
      { value: "sent", label: "Inviato" },
      { value: "accepted", label: "Accettato" },
      { value: "rejected", label: "Rifiutato" },
      { value: "invoiced", label: "Fatturato" }
    ],
    invoices: [
      { value: "", label: "Tutti" },
      { value: "issued", label: "Emessa" },
      { value: "paid", label: "Pagata" },
      { value: "cancelled", label: "Annullata" }
    ],
    supplier_orders: [
      { value: "", label: "Tutti" },
      { value: "draft", label: "Bozza" },
      { value: "sent", label: "Inviato" },
      { value: "confirmed", label: "Confermato" },
      { value: "shipped", label: "Spedito" },
      { value: "received", label: "Ricevuto" }
    ]
  };

  const getTotalResults = () => {
    return Object.values(results).reduce((sum, arr) => sum + arr.length, 0);
  };

  const getTotalAmount = () => {
    let total = 0;
    if (results.quotes) {
      total += results.quotes.reduce((sum, q) => sum + (q.total || 0), 0);
    }
    if (results.invoices) {
      total += results.invoices.reduce((sum, i) => sum + (i.total || 0), 0);
    }
    return total;
  };

  const renderDocumentCard = (doc, type) => {
    const icons = {
      appointments: Calendar,
      acceptances: ClipboardCheck,
      quotes: FileText,
      invoices: Receipt,
      supplier_orders: Package
    };
    const Icon = icons[type];

    const getTitle = () => {
      switch (type) {
        case "appointments":
          return `${doc.customer_name} - ${doc.appointment_date}`;
        case "acceptances":
          return `${doc.vehicle_plate} - ${doc.vehicle_brand} ${doc.vehicle_model}`;
        case "quotes":
          return `Preventivo #${doc.quote_number}`;
        case "invoices":
          return `Fattura #${doc.invoice_number}`;
        case "supplier_orders":
          return `Ordine #${doc.order_number}`;
        default:
          return "Documento";
      }
    };

    const getSubtitle = () => {
      switch (type) {
        case "appointments":
          return `${doc.service_type} - ${doc.appointment_time}`;
        case "acceptances":
          return doc.customer_name;
        case "quotes":
        case "invoices":
          return `${doc.customer_name} - ${doc.vehicle_plate}`;
        case "supplier_orders":
          return doc.supplier_name;
        default:
          return "";
      }
    };

    return (
      <div key={doc[`${type.slice(0, -1)}_id`] || doc.appointment_id || doc.acceptance_id} className="doc-card">
        <div className="flex items-start justify-between mb-2">
          <Icon className="w-5 h-5 text-primary" />
          <span className={`px-2 py-0.5 rounded-sm text-xs uppercase status-${doc.status}`}>
            {doc.status}
          </span>
        </div>
        <h4 className="text-white font-medium">{getTitle()}</h4>
        <p className="text-zinc-500 text-sm mt-1">{getSubtitle()}</p>
        {doc.total && (
          <p className="text-primary font-bold font-['JetBrains_Mono'] mt-2">€ {doc.total.toFixed(2)}</p>
        )}
        <p className="text-zinc-600 text-xs mt-2">{doc.created_at?.split("T")[0]}</p>
      </div>
    );
  };

  return (
    <div data-testid="archive-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Archivio Documenti</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="border-zinc-700"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filtri Avanzati
          </Button>
          {getTotalResults() > 0 && (
            <Button onClick={exportResults} className="btn-primary">
              <Download className="w-4 h-4 mr-2" />
              Esporta
            </Button>
          )}
        </div>
      </div>

      {/* Archivio PDF immutabile */}
      <PdfArchiveSection />

      {/* Quick Search Bar */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            data-testid="search-input"
            type="text"
            className="form-input pl-10"
            placeholder="Cerca per nome cliente, targa, numero documento..."
            value={filters.search || filters.customer_name}
            onChange={(e) => setFilters({...filters, search: e.target.value, customer_name: e.target.value})}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </div>
        <Select value={filters.doc_type} onValueChange={(val) => setFilters({...filters, doc_type: val})}>
          <SelectTrigger className="w-48 bg-zinc-900 border-zinc-800">
            <SelectValue placeholder="Tipo documento" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            {docTypes.map(type => (
              <SelectItem key={type.value} value={type.value || "all"}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button data-testid="search-btn" onClick={handleSearch} className="btn-primary">
          Cerca
        </Button>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
              <Filter className="w-5 h-5 text-primary" />
              Filtri Avanzati
            </h3>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-400">
              <X className="w-4 h-4 mr-1" /> Reset Filtri
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Customer Selection */}
            <div className="form-group">
              <label className="form-label">Cliente</label>
              <select
                className="form-select"
                value={filters.customer_id}
                onChange={(e) => setFilters({...filters, customer_id: e.target.value})}
              >
                <option value="">Tutti i clienti</option>
                {customers.map(c => (
                  <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Vehicle Plate */}
            <div className="form-group">
              <label className="form-label">Targa Veicolo</label>
              <input
                type="text"
                className="form-input uppercase"
                value={filters.vehicle_plate}
                onChange={(e) => setFilters({...filters, vehicle_plate: e.target.value.toUpperCase()})}
                placeholder="AA000AA"
              />
            </div>

            {/* Status */}
            <div className="form-group">
              <label className="form-label">Stato</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                {(statusOptions[filters.doc_type] || statusOptions.quotes).map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Date From */}
            <div className="form-group">
              <label className="form-label">Data Da</label>
              <input
                type="date"
                className="form-input"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value})}
              />
            </div>

            {/* Date To */}
            <div className="form-group">
              <label className="form-label">Data A</label>
              <input
                type="date"
                className="form-input"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value})}
              />
            </div>

            {/* Min Amount */}
            <div className="form-group">
              <label className="form-label flex items-center gap-1">
                <Euro className="w-3 h-3" /> Importo Min
              </label>
              <input
                type="number"
                className="form-input"
                value={filters.min_amount}
                onChange={(e) => setFilters({...filters, min_amount: e.target.value})}
                placeholder="0.00"
                step="0.01"
              />
            </div>

            {/* Max Amount */}
            <div className="form-group">
              <label className="form-label flex items-center gap-1">
                <Euro className="w-3 h-3" /> Importo Max
              </label>
              <input
                type="number"
                className="form-input"
                value={filters.max_amount}
                onChange={(e) => setFilters({...filters, max_amount: e.target.value})}
                placeholder="10000.00"
                step="0.01"
              />
            </div>

            {/* Sort */}
            <div className="form-group">
              <label className="form-label">Ordina per</label>
              <div className="flex gap-2">
                <select
                  className="form-select flex-1"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="created_at">Data</option>
                  <option value="total">Importo</option>
                  <option value="customer_name">Cliente</option>
                </select>
                <select
                  className="form-select w-24"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <option value="desc">↓</option>
                  <option value="asc">↑</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="spinner"></div>
        </div>
      ) : getTotalResults() > 0 ? (
        <div className="space-y-8">
          {/* Results Summary */}
          <div className="flex items-center justify-between text-zinc-400 bg-zinc-900/30 p-4 rounded-sm">
            <div>
              Trovati <span className="text-white font-bold">{getTotalResults()}</span> documenti
            </div>
            {getTotalAmount() > 0 && (
              <div className="flex items-center gap-2">
                <Euro className="w-4 h-4 text-primary" />
                <span className="text-white font-bold font-['JetBrains_Mono']">
                  {getTotalAmount().toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                </span>
                <span className="text-zinc-500 text-sm">totale</span>
              </div>
            )}
          </div>

          {/* Results by Type */}
          {Object.entries(results).map(([type, docs]) => {
            if (docs.length === 0) return null;
            const docType = docTypes.find(d => d.value === type);
            return (
              <div key={type}>
                <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] mb-4 flex items-center gap-2">
                  {docType?.icon && <docType.icon className="w-5 h-5 text-primary" />}
                  {docType?.label || type} ({docs.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {docs.map(doc => renderDocumentCard(doc, type))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <Search className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">Nessun risultato</p>
          <p className="empty-state-text">Prova a modificare i filtri di ricerca</p>
        </div>
      )}
    </div>
  );
}
